import React, { useEffect, useState, useRef } from 'react';
import useSpeechRecognition from '../hooks/useSpeechRecognition';
import { VoiceRecognitionProps } from '../types';

const VoiceRecorder: React.FC<VoiceRecognitionProps> = ({
  onResultsUpdate,
  onVoiceInputComplete,
  isListening,
  setIsListening
}) => {
  const {
    startListening,
    stopListening,
    transcript,
    interimResult,
    error
  } = useSpeechRecognition();

  const [isBrowserSupported, setIsBrowserSupported] = useState(true);
  const [micPermissionStatus, setMicPermissionStatus] = useState<string>('unknown');
  const [fullTranscript, setFullTranscript] = useState('');
  const [micTestInProgress, setMicTestInProgress] = useState(false);
  const micTestResourcesRef = useRef<{
    cleanup: () => void;
    audioStream?: MediaStream | null;
    audioContext?: AudioContext | null;
  }>({ cleanup: () => {} });
  
  // For interim result throttling
  const lastInterimProcessed = useRef<number>(0);
  const throttleTime = 1000; // Only process interim results once per second
  
  // Status message for user feedback
  const [statusMessage, setStatusMessage] = useState('');

  // Clean up mic test resources if component unmounts during test
  useEffect(() => {
    return () => {
      if (micTestResourcesRef.current && micTestResourcesRef.current.cleanup) {
        micTestResourcesRef.current.cleanup();
      }
    };
  }, []);

  // Force request microphone permission with user activation
  const forceRequestMicrophonePermission = async () => {
    try {
      // First, reset any previous denials that might be cached
      setStatusMessage('Requesting microphone access...');
      
      // Try a very simple permission request without timeouts
      // Important: This needs to be triggered by a user action (click)
      // which is why this should work better than auto-checking on load
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // If we get here, permission was granted
      stream.getTracks().forEach(track => track.stop()); // Release the microphone
      setMicPermissionStatus('granted');
      setStatusMessage('Microphone access granted! You can now start recording.');
      
      // Attempt to start the speech recognition engine again
      if (isListening) {
        startListening();
      }
    } catch (err) {
      console.error('Error requesting microphone permission:', err);
      setMicPermissionStatus('denied');
      setStatusMessage(`Failed to get microphone access: ${err instanceof Error ? err.message : 'Unknown error'}. Please check your browser settings.`);
    }
  };

  // Check if the browser supports speech recognition
  useEffect(() => {
    const checkBrowserSupport = async () => {
      const isSupported = 'SpeechRecognition' in window || 'webkitSpeechRecognition' in window;
      setIsBrowserSupported(isSupported);
      
      // Don't automatically request permissions on load
      // This often fails in Chrome due to new security restrictions
      // Instead, we'll wait for user interaction
      if (navigator.mediaDevices) {
        setStatusMessage('Click "Test Microphone" or "Start Recording" to begin.');
      } else {
        setMicPermissionStatus('unsupported');
        setStatusMessage('Your browser does not support microphone access.');
      }
      
      return isSupported;
    };

    checkBrowserSupport();
  }, []);
  
  // Cancel microphone test
  const cancelMicTest = () => {
    if (micTestResourcesRef.current && micTestResourcesRef.current.cleanup) {
      micTestResourcesRef.current.cleanup();
      setStatusMessage('Microphone test cancelled.');
      setMicTestInProgress(false);
    }
  };
  
  // Simple microphone test that's more likely to work with user activation
  const testMicrophone = async () => {
    // If a test is already in progress, cancel it first
    if (micTestInProgress) {
      cancelMicTest();
      // Short delay to ensure cleanup completes
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Set test in progress state
    setMicTestInProgress(true);
    setStatusMessage('Requesting microphone access...');
    
    try {
      // This simple getUserMedia call is more reliable than the complex audio analysis
      // and is enough to trigger the permission prompt in most browsers
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      setMicPermissionStatus('granted');
      setStatusMessage('✅ Microphone permission granted successfully!');
      
      // Create a simple audio meter to show it's working
      try {
        const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContext) {
          throw new Error('AudioContext not supported');
        }
        
        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(stream);
        microphone.connect(analyser);
        
        analyser.fftSize = 256;
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        
        setStatusMessage('Testing microphone levels...');
        
        // Check audio levels for 3 seconds
        let testActive = true;
        let hasSound = false;
        
        const checkAudio = () => {
          if (!testActive) return;
          
          analyser.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((acc, val) => acc + val, 0) / bufferLength;
          
          if (average > 5) {
            hasSound = true;
            setStatusMessage('✅ Microphone is working! Audio detected.');
          }
          
          if (testActive) {
            requestAnimationFrame(checkAudio);
          }
        };
        
        checkAudio();
        
        // Stop after 3 seconds
        setTimeout(() => {
          testActive = false;
          
          // Clean up
          stream.getTracks().forEach(track => track.stop());
          microphone.disconnect();
          audioContext.close();
          
          if (hasSound) {
            setStatusMessage('✅ Microphone test completed successfully. You can now start recording.');
          } else {
            setStatusMessage('⚠️ Microphone permission granted, but no audio detected. Check if your microphone is muted.');
          }
          
          setMicTestInProgress(false);
        }, 3000);
        
      } catch (audioErr) {
        // Even if audio processing fails, we still have microphone permission
        console.error('Audio processing error:', audioErr);
        stream.getTracks().forEach(track => track.stop());
        setStatusMessage('✅ Microphone permission granted, but audio testing failed.');
        setMicTestInProgress(false);
      }
      
    } catch (err) {
      console.error('Microphone permission error:', err);
      setMicPermissionStatus('denied');
      setStatusMessage(`Microphone access denied: ${err instanceof Error ? err.message : 'Unknown error'}. Check browser settings.`);
      setMicTestInProgress(false);
    }
  };

  // Handle error states
  useEffect(() => {
    if (error) {
      console.warn('Speech recognition error:', error);
      setStatusMessage(`Speech recognition error: ${error}. Try again or check browser permissions.`);
      
      // Update permission status if error indicates permission issue
      if (error.includes('not-allowed') || error.includes('permission') || error.includes('denied')) {
        setMicPermissionStatus('denied');
      }
      
      // Auto-stop listening if there's an error
      if (isListening) {
        setIsListening(false);
      }
    }
  }, [error, isListening, setIsListening]);

  useEffect(() => {
    if (isListening) {
      // Check if we already have permission
      if (micPermissionStatus === 'granted') {
        setStatusMessage('Listening...');
        startListening();
        setFullTranscript(''); // Reset transcript when starting to listen
      } else {
        // Request permission first
        navigator.mediaDevices.getUserMedia({ audio: true })
          .then(stream => {
            // Stop the tracks right away, we just needed the permission
            stream.getTracks().forEach(track => track.stop());
            setMicPermissionStatus('granted');
            setStatusMessage('Listening...');
            startListening();
            setFullTranscript(''); // Reset transcript when starting to listen
          })
          .catch(err => {
            console.error('Error getting mic permission:', err);
            setMicPermissionStatus('denied');
            setStatusMessage(`Microphone permission denied. Please allow access in your browser settings.`);
            setIsListening(false);
          });
      }
    } else {
      setStatusMessage(micPermissionStatus === 'granted' ? 'Ready to record' : 'Not listening');
      stopListening();
    }
  }, [isListening, startListening, stopListening, micPermissionStatus]);

  // Update final transcript when new segments are recognized
  useEffect(() => {
    if (transcript) {
      console.log('Final transcript updated:', transcript);
      setFullTranscript(transcript);
      // Process the transcript immediately
      onVoiceInputComplete(transcript);
      
      // Also update interim results for UI feedback
      onResultsUpdate({ transcript, isFinal: true });
    }
  }, [transcript, onResultsUpdate, onVoiceInputComplete]);

  // Handle interim results for real-time updates with throttling
  useEffect(() => {
    if (interimResult) {
      onResultsUpdate({ transcript: interimResult, isFinal: false });
      
      // Only process interim results at a controlled rate to reduce strain
      const now = Date.now();
      if (now - lastInterimProcessed.current > throttleTime) {
        // Also process interim results to show real-time flowchart updates
        const combinedTranscript = `${transcript} ${interimResult}`.trim();
        onVoiceInputComplete(combinedTranscript);
        lastInterimProcessed.current = now;
      }
    }
  }, [interimResult, onResultsUpdate, transcript, onVoiceInputComplete]);

  const handleToggleListening = () => {
    if (isListening) {
      setIsListening(false);
      // Final processing already handled in the transcript effect
    } else {
      setIsListening(true);
      lastInterimProcessed.current = 0; // Reset throttling timer
    }
  };

  if (!isBrowserSupported) {
    return (
      <div className="voice-recorder unsupported">
        <p>Speech recognition is not supported in your browser.</p>
        <p>Please use Chrome, Edge, or Safari for the best experience.</p>
      </div>
    );
  }

  return (
    <div className="voice-recorder">
      <button
        className={`microphone-button ${isListening ? 'listening' : ''}`}
        onClick={handleToggleListening}
        aria-label={isListening ? 'Stop recording' : 'Start recording'}
        disabled={micTestInProgress}
      >
        <svg 
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path 
            d="M12 14C13.66 14 15 12.66 15 11V5C15 3.34 13.66 2 12 2C10.34 2 9 3.34 9 5V11C9 12.66 10.34 14 12 14Z" 
            fill="currentColor" 
          />
          <path 
            d="M17 11C17 13.76 14.76 16 12 16C9.24 16 7 13.76 7 11H5C5 14.53 7.61 17.43 11 17.93V21H13V17.93C16.39 17.43 19 14.53 19 11H17Z" 
            fill="currentColor" 
          />
        </svg>
        {isListening ? 'Stop' : 'Start'} Recording
      </button>

      <div className="mic-test-button">
        {micTestInProgress ? (
          <div className="test-controls">
            <button onClick={cancelMicTest} className="cancel-test-button">
              Cancel Test
            </button>
            <div className="mic-test-spinner"></div>
          </div>
        ) : (
          <button onClick={testMicrophone}>
            Test Microphone
          </button>
        )}
      </div>

      {isListening && !error && (
        <div className="recording-indicator">
          <div className="pulse-ring"></div>
          <p>Listening...</p>
        </div>
      )}
      
      {statusMessage && (
        <div className={`status-message ${error ? 'error' : ''}`}>
          <p>{statusMessage}</p>
        </div>
      )}

      {micPermissionStatus === 'denied' && (
        <div className="permission-error">
          <p>Failed to get microphone access. Please allow access in your browser settings.</p>
          <button 
            className="retry-permission-button"
            onClick={forceRequestMicrophonePermission}
          >
            Request Microphone Permission Again
          </button>
          <div className="browser-specific-help">
            <p>To enable microphone access in Chrome:</p>
            <ol>
              <li>Click the padlock icon in the address bar</li>
              <li>Find "Microphone" and select "Allow"</li>
              <li>Refresh the page after changing settings</li>
            </ol>
          </div>
        </div>
      )}

      <div className="transcript-preview">
        {fullTranscript && (
          <>
            <p className="transcript-label">Recognized Text:</p>
            <p className="final-transcript">{fullTranscript}</p>
          </>
        )}
        {interimResult && (
          <>
            <p className="interim-label">Currently hearing:</p>
            <p className="interim-transcript">{interimResult}</p>
          </>
        )}
        {!fullTranscript && !interimResult && (
          <p className="empty-transcript">Your speech will appear here as you speak...</p>
        )}
      </div>
      
      {error && (
        <div className="browser-instructions">
          <p><strong>Troubleshooting:</strong></p>
          <ul>
            <li>Make sure your microphone is connected and working</li>
            <li>Allow microphone permissions in your browser settings</li>
            <li>Try using Chrome or Edge for best compatibility</li>
            <li>Check that no other application is using your microphone</li>
            <li>Speak clearly and at a normal volume</li>
            <li><strong>Important:</strong> Some browsers require reloading the page after granting permissions</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder; 